// Logs & traces: saved Logs Insights queries over the environment's Lambda log
// groups, bounded by the top-bar window; free text searches any substring.
import React from 'react';
import type { LogQueryResult, LogQuerySpec } from '../../../shared/types';
import { api } from '../api/client';
import { GAsyncState, GCard, GEyebrow, GH2, GPill, inputStyle, useAsyncTask } from '../design/atoms';
import { fmtBytes, fmtCount, fmtDateTime } from '../fmt';
import { useEnvStore } from '../store/envStore';
import { useResource } from '../store/resourceStore';

function cell(field: string, v: string): React.ReactNode {
  if (field === '@timestamp') return <span style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(v.replace(' ', 'T') + 'Z')}</span>;
  if (field === '@log') return <span title={v}>{v.split(':').pop()?.replace('/aws/lambda/', '')}</span>;
  if (field === '@message') return <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{v.replace(/\u001b\[[0-9;]*m/g, '').trim()}</span>;
  return v;
}

export function Logs() {
  const { env, window: win } = useEnvStore();
  const specs = useResource<LogQuerySpec[]>(`/api/env/${env}/logs`);
  const [queryId, setQueryId] = React.useState('errors');
  const [text, setText] = React.useState('');
  const [result, setResult] = React.useState<LogQueryResult | null>(null);
  const task = useAsyncTask();
  const spec = specs.data?.find((s) => s.id === queryId);

  const run = React.useCallback(() => {
    const qs = new URLSearchParams({ query: queryId, window: win, text });
    void task.run('Querying Logs Insights…', async () => {
      setResult(await api<LogQueryResult>(`/api/env/${env}/logs/run?${qs}`, {}, 28_000));
    });
  }, [env, queryId, win, text, task]);

  // run on first load and whenever env/window/query changes (free text runs on Enter)
  React.useEffect(() => { if (!spec?.needsText) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [env, win, queryId, spec?.needsText]);

  return (
    <div style={{ padding: '22px 28px 40px', maxWidth: 1500, display: 'flex', gap: 18, alignItems: 'flex-start' }}>
      <GCard padding="14px 10px" style={{ width: 260, flexShrink: 0 }}>
        <GEyebrow style={{ fontSize: 11.5, padding: '0 10px 8px', textTransform: 'uppercase', letterSpacing: 0.4 }}>Saved queries</GEyebrow>
        <GAsyncState busy={specs.loading && !specs.data} error={specs.error} onRetry={specs.reload} />
        {(specs.data ?? []).map((s) => {
          const on = s.id === queryId;
          return (
            <button key={s.id} className="row-hover" onClick={() => setQueryId(s.id)} aria-current={on || undefined} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 'var(--r-ctl)', background: on ? 'var(--bg-elev)' : 'transparent' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: on ? 'var(--fg)' : 'var(--fg-2)' }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11, color: 'var(--fg-4)', marginTop: 2 }}>{s.description}</div>
            </button>
          );
        })}
      </GCard>

      <div style={{ flex: 1, minWidth: 0 }}>
        <GCard padding="18px 20px">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <GH2 size={17}>{spec?.label ?? 'Logs'}</GH2>
            <GEyebrow style={{ fontSize: 12 }}>last {win} · {result ? `${result.logGroups.length} log groups` : ''}</GEyebrow>
            <div style={{ flex: 1 }} />
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { if (!spec?.needsText) setQueryId('text'); run(); } }}
              placeholder={spec?.needsText ? 'text to search for, then Enter' : 'free text (switches to Free text on Enter)'} style={{ ...inputStyle, width: 320, padding: '7px 12px' }} />
            <GPill onClick={run} disabled={task.busy || (!!spec?.needsText && !text.trim())} title={spec?.needsText && !text.trim() ? 'Type something to search for' : undefined}>Run</GPill>
          </div>
          <GAsyncState busy={task.busy} error={task.error} label={task.label} onRetry={task.retry} />
          {result && !task.busy && (
            <>
              <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-4)', marginBottom: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>{result.status}</span>
                <span>{fmtCount(result.stats.recordsMatched)} matched of {fmtCount(result.stats.recordsScanned)} scanned ({fmtBytes(result.stats.bytesScanned)})</span>
                <span>{result.rows.length} rows shown</span>
                <a href={result.consoleUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--fg-3)' }}>open Logs Insights ↗</a>
              </div>
              <details style={{ marginBottom: 12 }}>
                <summary style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-4)', cursor: 'pointer' }}>query text</summary>
                <pre style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-3)', whiteSpace: 'pre-wrap', margin: '6px 0 0', background: 'var(--bg-base)', padding: 10, borderRadius: 'var(--r-ctl)' }}>{result.queryText}</pre>
              </details>
              {result.rows.length === 0 ? <div style={{ fontFamily: 'var(--ff-code)', fontSize: 12.5, color: 'var(--fg-4)' }}>No matching lines in the last {win}.</div> : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="pd" style={{ fontSize: 12 }}>
                    <thead><tr>{result.fields.map((f) => <th key={f}>{f.replace(/^@/, '')}</th>)}</tr></thead>
                    <tbody>{result.rows.map((r, i) => <tr key={i}>{result.fields.map((f) => <td key={f} style={{ fontFamily: 'var(--ff-code)', fontSize: 12, verticalAlign: 'top' }}>{cell(f, r[f] ?? '')}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </GCard>
      </div>
    </div>
  );
}
